import type { SceneDef } from "../../genesis/schema";

/**
 * Le Parcours du Disciple — La Nouvelle Naissance (jeu 1, scène 2).
 * Rendered entirely from data by the WorldManager (Genesis engine).
 *
 * Signature mechanics (Jn 3:3), la traversée de la vallée sombre vers la Source :
 *  - Une vallée dans la pénombre du soir, encadrée de montagnes, qui descend
 *    vers la Source de lumière au nord : cascade, halo doré, fleurs de l'aube.
 *  - 7 fardeaux nommés (honte, culpabilité, tristesse, colère, inquiétude,
 *    regret, solitude) à ramasser (pickup) et à déposer dans le Bassin de la
 *    Source — l'eau qui lave et qui fait naître de nouveau.
 *  - 5 lanternes de l'aube (drag_drop) à placer sur le Porche de l'Aube pour
 *    baliser la sortie de la vallée.
 *  - Le Vent de l'Esprit (lever_action, Jn 3:8) qui souffle où il veut, et
 *    l'immersion dans les eaux vives de la Source (lever_action).
 *  - 2 voyageurs égarés dans la pénombre à conduire au Porche (guide_target).
 *  - PNJ : Nicodème venu de nuit (Jn 3:1-2), Jean le témoin de la lumière
 *    (Jn 1:7-8 ; 3:27-30).
 *
 * Ancres bibliques : Jn 3:3-8 (la nouvelle naissance), Ps 23:4 (la vallée),
 * Jn 1:7-8 (le témoin de la lumière), Ac 2:38 (vers le baptême, scène 3).
 * Cadrage d'espérance : la vallée est sombre mais JAMAIS sans issue — chaque
 * pas descend vers la lumière ; zéro effroi, zéro gore. Le Père n'est jamais
 * figuré : la Source est un lieu, un halo, une eau vive — pas un personnage.
 */
export const parcoursDuDiscipleScene2: SceneDef = {
  id: "parcours-du-disciple/scene-2",
  game: "parcours-du-disciple",
  title: { fr: "Le Parcours du Disciple — La Nouvelle Naissance", en: "The Disciple's Journey — The New Birth" },
  order: 2,
  keyName: { fr: "Clé de la Porte Étroite", en: "Key of the Narrow Gate" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 56,
    skyColor: "#3d4468",
    groundColor: "#4d5a45",
    lighting: "dusk",
    props: [
      // Rochers gris de la vallée dans la pénombre du soir.
      { id: "rochers_penombre", shape: "box", pos: [10, 0, 14], color: "#3a3d45", size: 0.9, scatter: 12, scatterRadius: 10 },
      // Arbres sombres qui bordent le fond de la vallée — silhouettes paisibles.
      { id: "arbres_penombre", shape: "tree", pos: [-12, 0, 12], color: "#2f4434", size: 1.7, scatter: 16, scatterRadius: 11 },
      // Stèles anciennes à demi effacées le long de l'ancien chemin.
      { id: "steles_vallee", shape: "stele", pos: [14, 0, 4], color: "#333640", size: 1.3, scatter: 6, scatterRadius: 8 },
      // Lucioles d'espérance qui flottent dans toute la vallée (y > 0).
      { id: "lucioles_esperance", shape: "flame", pos: [0, 1.5, 0], color: "#ffe08a", size: 0.22, scatter: 18, scatterRadius: 14 },
      // Montagnes qui encadrent la vallée à l'ouest.
      { id: "montagnes_ouest", shape: "mountain", pos: [-22, 0, -6], color: "#4b5563", size: 3, scatter: 5, scatterRadius: 9 },
      // Montagnes qui encadrent la vallée à l'est.
      { id: "montagnes_est", shape: "mountain", pos: [22, 0, -8], color: "#4b5563", size: 3, scatter: 5, scatterRadius: 9 },
      // La cascade de la Source, tout au nord — l'eau vive qui descend.
      { id: "cascade_source", shape: "water", pos: [0, 0, -26], color: "#7fc4e8", size: 1.3, scatter: 6, scatterRadius: 4 },
      // Halo doré suspendu au-dessus de la Source de lumière (y > 0).
      { id: "halo_source", shape: "flame", pos: [0, 3, -25], color: "#fff2b8", size: 0.6, scatter: 6, scatterRadius: 3 },
      // Fleurs pâles de l'aube qui s'ouvrent à mesure qu'on approche de la lumière.
      { id: "fleurs_aube", shape: "sphere", pos: [-6, 0, -18], color: "#d9c8ea", size: 0.26, scatter: 14, scatterRadius: 6 },
      // Pèlerins en chemin, descendant eux aussi vers la Source.
      { id: "pelerins_vallee", shape: "person", pos: [8, 0, 18], color: "#8a94a8", size: 1, scatter: 8, scatterRadius: 10 },
      // Roseaux autour du Bassin de la Source.
      { id: "roseaux_bassin", shape: "cone", pos: [4, 0, -21], color: "#5d7a5a", size: 0.5, scatter: 10, scatterRadius: 5 },
    ],
  },
  spawn: [0, 0, 22],
  difficulty: {
    // goalCount scale sur le VRAI total d'interactables avançants :
    // 7 pickup (fardeaux) + 5 drag_drop (lanternes) + 2 lever_action
    // + 2 guide_target = 16.
    D: { goalCount: 5, hints: true },
    A: { goalCount: 11, hints: true },
    E: { goalCount: 16, hints: false, timerS: 420 },
  },
  interactables: [
    // ——— Le Bassin de la Source : y déposer les fardeaux (l'eau qui lave) ———
    {
      id: "bassin_source", type: "build_slot",
      label: { fr: "Le Bassin de la Source de lumière", en: "The Basin of the Spring of light" },
      pos: [0, 0, -22], shape: "water", color: "#4c8fc4", size: 3, targetId: "bassin_source",
    },
    // ——— 7 fardeaux nommés, semés le long de la descente de la vallée ———
    {
      id: "fardeau_honte", type: "pickup",
      label: { fr: "Fardeau de la honte", en: "Burden of shame" },
      pos: [-5, 0, 16], shape: "box", color: "#6b4a52", size: 0.85, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_culpabilite", type: "pickup",
      label: { fr: "Fardeau de la culpabilité", en: "Burden of guilt" },
      pos: [5, 0, 15], shape: "box", color: "#5a4a6b", size: 0.85, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_tristesse", type: "pickup",
      label: { fr: "Fardeau de la tristesse", en: "Burden of sorrow" },
      pos: [-9, 0, 10], shape: "sphere", color: "#4a5a6b", size: 0.8, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_colere", type: "pickup",
      label: { fr: "Fardeau de la colère", en: "Burden of anger" },
      pos: [9, 0, 8], shape: "box", color: "#7a4a42", size: 0.85, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_inquietude", type: "pickup",
      label: { fr: "Fardeau de l'inquiétude", en: "Burden of worry" },
      pos: [-4, 0, 4], shape: "sphere", color: "#55606b", size: 0.8, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_regret", type: "pickup",
      label: { fr: "Fardeau du regret", en: "Burden of regret" },
      pos: [6, 0, 1], shape: "box", color: "#6b5a4a", size: 0.85, targetId: "bassin_source", count: 1,
    },
    {
      id: "fardeau_solitude", type: "pickup",
      label: { fr: "Fardeau de la solitude", en: "Burden of loneliness" },
      pos: [-8, 0, -3], shape: "sphere", color: "#4f4a5f", size: 0.8, targetId: "bassin_source", count: 1,
    },
    // ——— Le Porche de l'Aube : y placer 5 lanternes pour baliser la sortie ———
    {
      id: "porche_aube", type: "build_slot",
      label: { fr: "Le Porche de l'Aube", en: "The Porch of Dawn" },
      pos: [-4, 0, -20], shape: "gate", color: "#f2d47a", size: 3.4, targetId: "porche_aube",
    },
    {
      id: "lanterne_promesse", type: "drag_drop",
      label: { fr: "Lanterne de la promesse", en: "Lantern of the promise" },
      pos: [2, 0, 8], shape: "flame", color: "#f4d06f", size: 0.5, targetId: "porche_aube", count: 1,
    },
    {
      id: "lanterne_priere", type: "drag_drop",
      label: { fr: "Lanterne de la prière", en: "Lantern of prayer" },
      pos: [-2, 0, -1], shape: "flame", color: "#f0c45c", size: 0.5, targetId: "porche_aube", count: 1,
    },
    {
      id: "lanterne_confiance", type: "drag_drop",
      label: { fr: "Lanterne de la confiance", en: "Lantern of trust" },
      pos: [3, 0, -7], shape: "flame", color: "#f4d06f", size: 0.5, targetId: "porche_aube", count: 1,
    },
    {
      id: "lanterne_perseverance", type: "drag_drop",
      label: { fr: "Lanterne de la persévérance", en: "Lantern of perseverance" },
      pos: [-3, 0, -12], shape: "flame", color: "#f0c45c", size: 0.5, targetId: "porche_aube", count: 1,
    },
    {
      id: "lanterne_esperance", type: "drag_drop",
      label: { fr: "Lanterne de l'espérance", en: "Lantern of hope" },
      pos: [2, 0, -17], shape: "flame", color: "#f4d06f", size: 0.5, targetId: "porche_aube", count: 1,
    },
    // ——— Le Vent de l'Esprit (Jn 3:8) — se tenir dans le souffle ———
    {
      id: "vent_esprit", type: "lever_action",
      label: { fr: "Le Vent de l'Esprit (Jean 3:8)", en: "The Wind of the Spirit (John 3:8)" },
      pos: [8, 0, -14], shape: "cone", color: "#9fc4d8", size: 1.4, holdS: 2.5,
    },
    // ——— Les eaux vives de la Source — s'y tenir pour naître d'en haut ———
    {
      id: "immersion_source", type: "lever_action",
      label: { fr: "Les Eaux vives de la Source", en: "The Living waters of the Spring" },
      pos: [3, 0, -24], shape: "water", color: "#5aa7d8", size: 2.4, holdS: 3,
    },
    // ——— Voyageurs égarés dans la pénombre, à conduire au Porche ———
    {
      id: "voyageur_ouest", type: "guide_target",
      label: { fr: "Voyageur égaré du versant ouest", en: "Lost traveler of the west slope" },
      pos: [-14, 0, 6], shape: "person", color: "#8a8fa0", size: 1, targetId: "porche_aube", count: 1,
    },
    {
      id: "voyageuse_est", type: "guide_target",
      label: { fr: "Voyageuse égarée du versant est", en: "Lost traveler of the east slope" },
      pos: [14, 0, -4], shape: "person", color: "#9a90a8", size: 1, targetId: "porche_aube", count: 1,
    },
    // ——— PNJ nommés ———
    {
      id: "nicodeme", type: "npc_dialog",
      label: { fr: "Nicodème, venu de nuit (Jean 3:1-2)", en: "Nicodemus, come by night (John 3:1-2)" },
      pos: [-6, 0, -16], shape: "person", color: "#7a6ba0", size: 1.1,
    },
    {
      id: "jean_temoin", type: "npc_dialog",
      label: { fr: "Jean, le témoin de la lumière (Jean 1:7-8)", en: "John, the witness of the light (John 1:7-8)" },
      pos: [10, 0, -20], shape: "person", color: "#6b8a7a", size: 1.1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panel_jn3_naissance", type: "panel",
      label: { fr: "Naître de nouveau", en: "Born anew" },
      pos: [-3, 0, 11], shape: "stele", color: "#9fb0c4", size: 1.3, panelId: "p_jn3_naissance",
    },
    {
      id: "panel_ps23_vallee", type: "panel",
      label: { fr: "La vallée traversée, jamais seul", en: "Through the valley, never alone" },
      pos: [7, 0, -10], shape: "stele", color: "#b9a06a", size: 1.3, panelId: "p_ps23_vallee",
    },
  ],
  objectives: [
    {
      id: "obj_fardeaux_source", kind: "collect",
      label: { fr: "Dépose les 7 fardeaux de la vallée dans le Bassin de la Source", en: "Lay the 7 burdens of the valley into the Basin of the Spring" },
      target: "bassin_source", count: 7,
    },
    {
      id: "obj_atteindre_source", kind: "reach",
      label: { fr: "Traverse la vallée sombre et tiens-toi dans les eaux vives de la Source", en: "Cross the dark valley and stand in the Living waters of the Spring" },
      target: "immersion_source",
    },
  ],
  panels: [
    {
      id: "p_jn3_naissance",
      title: { fr: "Naître de nouveau", en: "Born anew" },
      reference: "Jean 3:3",
      passage: {
        fr: "Jésus lui répondit: En vérité, en vérité, je te le dis, si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu.",
        en: "Jesus answered him, \"Most certainly, I tell you, unless one is born anew, he can't see God's Kingdom.\"",
      },
      explain: {
        fr: "Nicodème arrive de nuit, comme toi au fond de cette vallée. Jésus ne lui demande pas de porter ses fardeaux plus courageusement : il lui annonce une vie entièrement nouvelle, donnée d'en haut par l'Esprit. Déposer chaque fardeau dans l'eau de la Source raconte ce commencement : on ne s'améliore pas, on naît de nouveau.",
        en: "Nicodemus arrives by night, like you at the bottom of this valley. Jesus does not ask him to carry his burdens more bravely: he announces an entirely new life, given from above by the Spirit. Laying each burden into the water of the Spring tells that beginning: we are not improved, we are born anew.",
      },
      didYouKnow: {
        fr: "Le mot grec « anôthen » signifie à la fois « de nouveau » et « d'en haut » : la nouvelle naissance ne monte pas de nos efforts, elle descend comme un don.",
        en: "The Greek word \"anothen\" means both \"anew\" and \"from above\": the new birth does not rise from our efforts, it comes down as a gift.",
      },
      application: {
        fr: "Quel fardeau nommé dans cette vallée ressemble le plus au tien ? Nomme-le devant Dieu : c'est le premier pas vers la Source.",
        en: "Which named burden in this valley looks most like yours? Name it before God: that is the first step toward the Spring.",
      },
    },
    {
      id: "p_ps23_vallee",
      title: { fr: "La vallée traversée, jamais seul", en: "Through the valley, never alone" },
      reference: "Psaume 23:4",
      passage: {
        fr: "Quand je marche dans la vallée de l'ombre de la mort, Je ne crains aucun mal, car tu es avec moi: Ta houlette et ton bâton me rassurent.",
        en: "Even though I walk through the valley of the shadow of death, I will fear no evil, for you are with me. Your rod and your staff, they comfort me.",
      },
      explain: {
        fr: "Le psaume ne promet pas une route sans vallée : il promet une présence dans la vallée. David ne dit pas « je contourne », mais « je marche » — la vallée se traverse, elle ne se fuit pas, et elle débouche sur la lumière. Les lucioles et les lanternes de ce chemin rappellent que même la pénombre est habitée d'espérance.",
        en: "The psalm does not promise a road without a valley: it promises a presence in the valley. David does not say \"I go around\" but \"I walk through\" — the valley is crossed, not fled, and it opens onto the light. The fireflies and lanterns of this path remind us that even the dusk is inhabited by hope.",
      },
      didYouKnow: {
        fr: "La houlette et le bâton étaient les deux outils du berger : l'une pour ramener la brebis, l'autre pour la protéger. Deux images de soin, aucune de menace.",
        en: "The rod and the staff were the shepherd's two tools: one to bring the sheep back, the other to protect it. Two images of care, none of threat.",
      },
      application: {
        fr: "Dans quelle « vallée » marches-tu en ce moment ? Redis ce verset à voix basse en plaçant une lanterne : « tu es avec moi ».",
        en: "Which \"valley\" are you walking through right now? Whisper this verse as you place a lantern: \"you are with me\".",
      },
    },
  ],
  quizzes: [
    {
      id: "q_naitre", level: "D",
      prompt: { fr: "Que dit Jésus à Nicodème sur ce qu'il faut pour voir le royaume de Dieu (Jean 3:3) ?", en: "What does Jesus tell Nicodemus is needed to see God's Kingdom (John 3:3)?" },
      choices: [
        { fr: "Naître de nouveau", en: "Be born anew" },
        { fr: "Porter ses fardeaux plus longtemps", en: "Carry one's burdens longer" },
        { fr: "Traverser la vallée sans s'arrêter", en: "Cross the valley without stopping" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu » (Jean 3:3). La vie nouvelle est un don d'en haut, pas une performance.",
        en: "\"Unless one is born anew, he can't see God's Kingdom\" (John 3:3). The new life is a gift from above, not a performance.",
      },
    },
    {
      id: "q_vent", level: "A",
      prompt: { fr: "À quoi Jésus compare-t-il l'Esprit dans Jean 3:8 ?", en: "What does Jesus compare the Spirit to in John 3:8?" },
      choices: [
        { fr: "À une montagne immobile", en: "To an unmoving mountain" },
        { fr: "Au vent qui souffle où il veut", en: "To the wind that blows where it wants" },
        { fr: "À une lampe posée sur une table", en: "To a lamp set on a table" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Le vent souffle où il veut, et tu en entends le bruit ; mais tu ne sais d'où il vient, ni où il va » (Jean 3:8). On ne commande pas l'Esprit ; on se laisse conduire par lui.",
        en: "\"The wind blows where it wants, and you hear its sound, but don't know where it comes from and where it is going\" (John 3:8). We do not command the Spirit; we let ourselves be led by him.",
      },
    },
    {
      id: "q_nicodeme_nuit", level: "E",
      prompt: { fr: "À quel moment Nicodème vient-il trouver Jésus, et que révèle ce détail (Jean 3:1-2) ?", en: "When does Nicodemus come to Jesus, and what does that detail reveal (John 3:1-2)?" },
      choices: [
        { fr: "En plein jour, devant la foule, pour le mettre à l'épreuve", en: "In broad daylight, before the crowd, to test him" },
        { fr: "De nuit, avec ses vraies questions — et Jésus l'accueille", en: "By night, with his real questions — and Jesus welcomes him" },
        { fr: "Pendant une fête, par simple curiosité", en: "During a feast, out of mere curiosity" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Nicodème, chef des Juifs, vient « de nuit » (Jean 3:2) : la démarche discrète d'un cœur qui cherche sincèrement. Jésus ne le rejette pas — c'est à lui qu'il confie l'annonce de la nouvelle naissance, et Nicodème reparaîtra en plein jour (Jean 19:39).",
        en: "Nicodemus, a ruler of the Jews, comes \"by night\" (John 3:2): the discreet step of a heart that sincerely seeks. Jesus does not turn him away — to him he entrusts the announcement of the new birth, and Nicodemus will reappear in broad daylight (John 19:39).",
      },
    },
    {
      id: "qD2", level: "D",
      prompt: { fr: "Dans le Psaume 23:4, pourquoi le psalmiste ne craint-il aucun mal dans la vallée ?", en: "In Psalm 23:4, why does the psalmist fear no evil in the valley?" },
      choices: [
        { fr: "Parce qu'il connaît le chemin par cœur", en: "Because he knows the road by heart" },
        { fr: "Parce que la vallée n'est pas vraiment sombre", en: "Because the valley is not really dark" },
        { fr: "Parce que le Berger est avec lui : « tu es avec moi »", en: "Because the Shepherd is with him: \"you are with me\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Je ne crains aucun mal, car tu es avec moi » (Psaume 23:4). Ce n'est pas l'absence de vallée qui rassure, c'est la présence du Berger dans la vallée.",
        en: "\"I will fear no evil, for you are with me\" (Psalm 23:4). It is not the absence of a valley that comforts, but the Shepherd's presence in the valley.",
      },
    },
    {
      id: "qD3", level: "D",
      prompt: { fr: "Qui vient trouver Jésus de nuit avec ses questions (Jean 3:1-2) ?", en: "Who comes to Jesus by night with his questions (John 3:1-2)?" },
      choices: [
        { fr: "Nicodème, un chef des Juifs", en: "Nicodemus, a ruler of the Jews" },
        { fr: "André, le frère de Simon", en: "Andrew, Simon's brother" },
        { fr: "Jean, le témoin de la lumière", en: "John, the witness of the light" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il y eut un homme d'entre les pharisiens, nommé Nicodème, un chef des Juifs, qui vint, lui, auprès de Jésus, de nuit » (Jean 3:1-2). Jésus accueille celui qui cherche, même à la nuit tombée.",
        en: "\"Now there was a man of the Pharisees named Nicodemus, a ruler of the Jews. He came to Jesus by night\" (John 3:1-2). Jesus welcomes the seeker, even after nightfall.",
      },
    },
    {
      id: "qD4", level: "D",
      prompt: { fr: "Selon Jean 3:8, que sait-on du vent auquel l'Esprit est comparé ?", en: "According to John 3:8, what do we know of the wind the Spirit is compared to?" },
      choices: [
        { fr: "On sait exactement d'où il vient et où il va", en: "We know exactly where it comes from and where it goes" },
        { fr: "On en entend le bruit, sans savoir d'où il vient ni où il va", en: "We hear its sound, without knowing where it comes from or where it goes" },
        { fr: "Il ne souffle que sur les montagnes", en: "It only blows on the mountains" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Le vent souffle où il veut, et tu en entends le bruit ; mais tu ne sais d'où il vient, ni où il va » (Jean 3:8). L'Esprit ne se commande pas : il se reçoit.",
        en: "\"The wind blows where it wants, and you hear its sound, but don't know where it comes from and where it is going\" (John 3:8). The Spirit is not commanded: he is received.",
      },
    },
    {
      id: "qD5", level: "D",
      prompt: { fr: "Dans le Psaume 23:4, quels objets du berger rassurent le psalmiste ?", en: "In Psalm 23:4, which of the shepherd's tools comfort the psalmist?" },
      choices: [
        { fr: "Sa lampe et sa corde", en: "His lamp and his rope" },
        { fr: "Sa fronde et son épée", en: "His sling and his sword" },
        { fr: "Sa houlette et son bâton", en: "His rod and his staff" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ta houlette et ton bâton me rassurent » (Psaume 23:4). Les deux outils du berger servent à ramener et à protéger la brebis — deux images de soin, aucune de menace.",
        en: "\"Your rod and your staff, they comfort me\" (Psalm 23:4). The shepherd's two tools bring back and protect the sheep — two images of care, none of threat.",
      },
    },
    {
      id: "qA2", level: "A",
      prompt: { fr: "Selon Jean 1:7-8, quel était le rôle de Jean ?", en: "According to John 1:7-8, what was John's role?" },
      choices: [
        { fr: "Être lui-même la lumière du monde", en: "To be himself the light of the world" },
        { fr: "Rendre témoignage à la lumière, sans être la lumière", en: "To bear witness to the light, without being the light" },
        { fr: "Garder la lumière cachée jusqu'au bon moment", en: "To keep the light hidden until the right time" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il vint pour servir de témoin, pour rendre témoignage à la lumière… Il n'était pas la lumière, mais il parut pour rendre témoignage à la lumière » (Jean 1:7-8). Le témoin montre la lumière, il ne se montre pas lui-même.",
        en: "\"He came as a witness, that he might testify about the light… He was not the light, but was sent that he might testify about the light\" (John 1:7-8). The witness points to the light, not to himself.",
      },
    },
    {
      id: "qA3", level: "A",
      prompt: { fr: "Le mot grec « anôthen » employé en Jean 3:3 a un double sens : lequel ?", en: "The Greek word \"anothen\" used in John 3:3 has a double meaning: which one?" },
      choices: [
        { fr: "« De nouveau » et « d'en haut »", en: "\"Anew\" and \"from above\"" },
        { fr: "« Dans l'eau » et « dans le feu »", en: "\"In water\" and \"in fire\"" },
        { fr: "« Aujourd'hui » et « demain »", en: "\"Today\" and \"tomorrow\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Anôthen » signifie à la fois « de nouveau » et « d'en haut » : la nouvelle naissance de Jean 3:3 ne monte pas de nos efforts, elle descend d'en haut comme un don.",
        en: "\"Anothen\" means both \"anew\" and \"from above\": the new birth of John 3:3 does not rise from our efforts, it comes down from above as a gift.",
      },
    },
    {
      id: "qA4", level: "A",
      prompt: { fr: "Selon Jean 3:5, de quoi faut-il naître pour entrer dans le royaume de Dieu ?", en: "According to John 3:5, of what must one be born to enter God's Kingdom?" },
      choices: [
        { fr: "De la chair et du sang", en: "Of flesh and blood" },
        { fr: "De la loi et des œuvres", en: "Of the law and of works" },
        { fr: "D'eau et d'Esprit", en: "Of water and Spirit" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Si un homme ne naît d'eau et d'Esprit, il ne peut entrer dans le royaume de Dieu » (Jean 3:5). Les eaux vives de la Source et le Vent de l'Esprit de cette vallée racontent ces deux signes.",
        en: "\"Unless one is born of water and Spirit, he can't enter into God's Kingdom\" (John 3:5). The Spring's living waters and the Wind of the Spirit in this valley tell of these two signs.",
      },
    },
    {
      id: "qA5", level: "A",
      prompt: { fr: "Dans le Psaume 23:4, que fait David face à la vallée de l'ombre ?", en: "In Psalm 23:4, what does David do when facing the valley of shadow?" },
      choices: [
        { fr: "Il la traverse en marchant : « quand je marche dans la vallée »", en: "He walks through it: \"even though I walk through the valley\"" },
        { fr: "Il la contourne prudemment", en: "He carefully goes around it" },
        { fr: "Il attend au bord que la nuit passe", en: "He waits at the edge for the night to pass" },
      ],
      answerIndex: 0,
      explain: {
        fr: "David ne dit pas « je contourne » mais « quand je marche dans la vallée de l'ombre de la mort » (Psaume 23:4). La vallée se traverse, elle ne se fuit pas — et elle débouche sur la lumière.",
        en: "David does not say \"I go around\" but \"even though I walk through the valley of the shadow of death\" (Psalm 23:4). The valley is crossed, not fled — and it opens onto the light.",
      },
    },
    {
      id: "qE2", level: "E",
      prompt: { fr: "Où Nicodème reparaît-il à la fin de l'Évangile de Jean (Jean 19:39) ?", en: "Where does Nicodemus reappear at the end of John's Gospel (John 19:39)?" },
      choices: [
        { fr: "Il renie Jésus dans la cour du grand prêtre", en: "He denies Jesus in the high priest's courtyard" },
        { fr: "Il apporte myrrhe et aloès pour l'ensevelissement de Jésus, en plein jour", en: "He brings myrrh and aloes for Jesus' burial, in broad daylight" },
        { fr: "Il retourne interroger Jésus une seconde nuit", en: "He returns to question Jesus a second night" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Celui qui était venu « de nuit » reparaît au grand jour : « Nicodème… apporta un mélange d'environ cent livres de myrrhe et d'aloès » pour ensevelir Jésus (Jean 19:39). La graine semée dans la nuit a porté son fruit.",
        en: "The one who had come \"by night\" reappears in daylight: \"Nicodemus… came bringing a mixture of myrrh and aloes, about a hundred pounds\" for Jesus' burial (John 19:39). The seed sown in the night bore its fruit.",
      },
    },
    {
      id: "qE3", level: "E",
      prompt: { fr: "Que déclare Jean au sujet de Jésus en Jean 3:30 ?", en: "What does John declare about Jesus in John 3:30?" },
      choices: [
        { fr: "« Il faut qu'il croisse, et que je diminue »", en: "\"He must increase, but I must decrease\"" },
        { fr: "« Je suis plus grand que lui »", en: "\"I am greater than he\"" },
        { fr: "« Nous croîtrons tous les deux ensemble »", en: "\"We will both increase together\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il faut qu'il croisse, et que je diminue » (Jean 3:30). La joie du témoin est de s'effacer devant celui qu'il annonce — comme une lanterne s'efface devant l'aube.",
        en: "\"He must increase, but I must decrease\" (John 3:30). The witness's joy is to step back before the one he announces — as a lantern fades before the dawn.",
      },
    },
    {
      id: "qD6", level: "D",
      prompt: { fr: "Comment David appelle-t-il l'Éternel au début du Psaume 23 (v. 1) ?", en: "What does David call the LORD at the start of Psalm 23 (v. 1)?" },
      choices: [
        { fr: "Mon rocher inaccessible", en: "My unreachable rock" },
        { fr: "Mon berger : « je ne manquerai de rien »", en: "My shepherd: \"I shall lack nothing\"" },
        { fr: "Mon juge sévère", en: "My stern judge" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« L'Éternel est mon berger: je ne manquerai de rien » (Psaume 23:1). Toute la traversée de la vallée repose sur ce premier mot de confiance : un Berger qui pourvoit.",
        en: "\"The LORD is my shepherd: I shall lack nothing\" (Psalm 23:1). The whole crossing of the valley rests on that first word of trust: a Shepherd who provides.",
      },
    },
    {
      id: "qD7", level: "D",
      prompt: { fr: "Selon Jean 3:16, pourquoi Dieu a-t-il donné son Fils unique ?", en: "According to John 3:16, why did God give his one and only Son?" },
      choices: [
        { fr: "Afin que quiconque croit en lui ne périsse point, mais qu'il ait la vie éternelle", en: "So that whoever believes in him should not perish, but have eternal life" },
        { fr: "Pour juger le monde sans délai", en: "To judge the world without delay" },
        { fr: "Pour récompenser les plus religieux", en: "To reward the most religious" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Car Dieu a tant aimé le monde qu'il a donné son Fils unique, afin que quiconque croit en lui ne périsse point, mais qu'il ait la vie éternelle » (Jean 3:16). La nouvelle naissance jaillit de l'amour de Dieu, pas de la peur.",
        en: "\"For God so loved the world, that he gave his one and only Son, that whoever believes in him should not perish, but have eternal life\" (John 3:16). The new birth springs from God's love, not from fear.",
      },
    },
    {
      id: "qD8", level: "D",
      prompt: { fr: "Où le Berger du Psaume 23:2 fait-il reposer sa brebis ?", en: "Where does the Shepherd of Psalm 23:2 make his sheep rest?" },
      choices: [
        { fr: "Sur les sommets balayés par le vent", en: "On wind-swept peaks" },
        { fr: "Au milieu du désert", en: "In the middle of the desert" },
        { fr: "Dans de verts pâturages, près des eaux paisibles", en: "In green pastures, beside still waters" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il me fait reposer dans de verts pâturages, Il me dirige près des eaux paisibles » (Psaume 23:2). Avant même la vallée, le psaume commence par le repos : le Berger conduit vers ce qui restaure.",
        en: "\"He makes me lie down in green pastures. He leads me beside still waters\" (Psalm 23:2). Even before the valley, the psalm begins with rest: the Shepherd leads toward what restores.",
      },
    },
    {
      id: "qD9", level: "D",
      prompt: { fr: "Que reconnaît Nicodème au sujet de Jésus dès sa première parole (Jean 3:2) ?", en: "What does Nicodemus acknowledge about Jesus in his very first words (John 3:2)?" },
      choices: [
        { fr: "Qu'il est un prophète venu d'Égypte", en: "That he is a prophet come from Egypt" },
        { fr: "Qu'il est un docteur venu de Dieu, car nul ne peut faire ses miracles si Dieu n'est avec lui", en: "That he is a teacher come from God, for no one can do his signs unless God is with him" },
        { fr: "Qu'il est un maître comme les autres", en: "That he is a teacher like any other" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Rabbi, nous savons que tu es un docteur venu de Dieu; car personne ne peut faire ces miracles que tu fais, si Dieu n'est avec lui » (Jean 3:2). Nicodème part de ce qu'il a vu — et Jésus le conduit plus loin : vers la nouvelle naissance.",
        en: "\"Rabbi, we know that you are a teacher come from God, for no one can do these signs that you do, unless God is with him\" (John 3:2). Nicodemus starts from what he has seen — and Jesus leads him further: to the new birth.",
      },
    },
    {
      id: "qA6", level: "A",
      prompt: { fr: "À quel épisode du désert Jésus compare-t-il son élévation (Jean 3:14) ?", en: "To which wilderness episode does Jesus compare his being lifted up (John 3:14)?" },
      choices: [
        { fr: "Au serpent d'airain élevé par Moïse (Nombres 21:9)", en: "To the bronze serpent lifted up by Moses (Numbers 21:9)" },
        { fr: "À la manne descendue du ciel", en: "To the manna come down from heaven" },
        { fr: "Au rocher frappé par Moïse", en: "To the rock struck by Moses" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Et comme Moïse éleva le serpent dans le désert, il faut de même que le Fils de l'homme soit élevé, afin que quiconque croit en lui ait la vie éternelle » (Jean 3:14-15). Quiconque regardait le serpent d'airain vivait (Nombres 21:9) ; quiconque regarde au Fils élevé reçoit la vie.",
        en: "\"As Moses lifted up the serpent in the wilderness, even so must the Son of Man be lifted up, that whoever believes in him… may have eternal life\" (John 3:14-15). Whoever looked at the bronze serpent lived (Numbers 21:9); whoever looks to the lifted-up Son receives life.",
      },
    },
    {
      id: "qA7", level: "A",
      prompt: { fr: "À qui Jean se compare-t-il pour dire sa joie devant Jésus (Jean 3:29) ?", en: "To whom does John compare himself to express his joy before Jesus (John 3:29)?" },
      choices: [
        { fr: "Au maître de la fête des noces", en: "To the master of the wedding feast" },
        { fr: "À l'époux lui-même", en: "To the bridegroom himself" },
        { fr: "À l'ami de l'époux, qui se réjouit d'entendre sa voix", en: "To the friend of the bridegroom, who rejoices to hear his voice" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Celui qui a l'épouse, c'est l'époux; mais l'ami de l'époux… éprouve une grande joie à cause de la voix de l'époux: aussi cette joie, qui est la mienne, est parfaite » (Jean 3:29). Le témoin trouve sa joie pleine dans la joie d'un autre.",
        en: "\"He who has the bride is the bridegroom; but the friend of the bridegroom… rejoices greatly because of the bridegroom's voice. This, my joy, therefore is made full\" (John 3:29). The witness finds his full joy in another's joy.",
      },
    },
    {
      id: "qA8", level: "A",
      prompt: { fr: "Selon le Psaume 23:6, qu'est-ce qui accompagnera David tous les jours de sa vie ?", en: "According to Psalm 23:6, what will follow David all the days of his life?" },
      choices: [
        { fr: "L'ombre de la vallée", en: "The shadow of the valley" },
        { fr: "Le bonheur et la grâce", en: "Goodness and loving kindness" },
        { fr: "La crainte de l'ennemi", en: "The fear of the enemy" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Oui, le bonheur et la grâce m'accompagneront tous les jours de ma vie, et j'habiterai dans la maison de l'Éternel jusqu'à la fin de mes jours » (Psaume 23:6). La vallée n'a pas le dernier mot : le psaume s'achève dans la maison de l'Éternel.",
        en: "\"Surely goodness and loving kindness shall follow me all the days of my life, and I will dwell in the LORD's house forever\" (Psalm 23:6). The valley does not have the last word: the psalm ends in the house of the LORD.",
      },
    },
    {
      id: "qE4", level: "E",
      prompt: { fr: "Entre la nuit de Jean 3 et l'ensevelissement de Jean 19, où Nicodème reparaît-il (Jean 7:50-51) ?", en: "Between the night of John 3 and the burial of John 19, where does Nicodemus reappear (John 7:50-51)?" },
      choices: [
        { fr: "Devant les pharisiens, où il demande qu'on écoute Jésus avant de le juger", en: "Before the Pharisees, asking that Jesus be heard before being judged" },
        { fr: "Sur la montagne de la transfiguration", en: "On the mount of transfiguration" },
        { fr: "Dans la barque pendant la tempête", en: "In the boat during the storm" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Nicodème… leur dit: Notre loi condamne-t-elle un homme avant qu'on l'entende et qu'on sache ce qu'il a fait ? » (Jean 7:50-51). Pas à pas, celui qui était venu de nuit apprend à parler en plein conseil — la graine grandit.",
        en: "\"Nicodemus… said to them, 'Does our law judge a man unless it first hears from him personally and knows what he does?'\" (John 7:50-51). Step by step, the one who came by night learns to speak in open council — the seed is growing.",
      },
    },
    {
      id: "qE5", level: "E",
      prompt: { fr: "Quelle promesse de l'Ancien Testament annonce déjà la nouvelle naissance (Ézéchiel 36:26) ?", en: "Which Old Testament promise already announces the new birth (Ezekiel 36:26)?" },
      choices: [
        { fr: "« Je vous donnerai un pays sans vallées »", en: "\"I will give you a land without valleys\"" },
        { fr: "« Je vous donnerai un cœur nouveau, et je mettrai en vous un esprit nouveau »", en: "\"I will give you a new heart, and I will put a new spirit within you\"" },
        { fr: "« Je vous donnerai des forces doublées »", en: "\"I will give you doubled strength\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Je vous donnerai un cœur nouveau, et je mettrai en vous un esprit nouveau » (Ézéchiel 36:26). Bien avant Nicodème, les prophètes annonçaient que Dieu ne répare pas l'ancien cœur : il en donne un nouveau.",
        en: "\"I will give you a new heart, and I will put a new spirit within you\" (Ezekiel 36:26). Long before Nicodemus, the prophets announced that God does not patch the old heart: he gives a new one.",
      },
    },
    {
      id: "qE6", level: "E",
      prompt: { fr: "Où Jean baptisait-il, et pourquoi ce lieu (Jean 3:23) ?", en: "Where was John baptizing, and why that place (John 3:23)?" },
      choices: [
        { fr: "À Jérusalem, près du temple", en: "In Jerusalem, near the temple" },
        { fr: "À Bethléem, près des bergeries", en: "In Bethlehem, near the sheepfolds" },
        { fr: "À Énon, près de Salim, parce qu'il y avait là beaucoup d'eau", en: "At Enon near Salim, because there was much water there" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Jean aussi baptisait à Énon, près de Salim, parce qu'il y avait là beaucoup d'eau; et on y venait pour être baptisé » (Jean 3:23). Le détail géographique dit le concret du chemin : là où l'eau abonde, la grâce donne rendez-vous.",
        en: "\"John also was baptizing in Enon near Salim, because there was much water there. They came, and were baptized\" (John 3:23). The geographic detail tells the concreteness of the way: where water abounds, grace sets a meeting place.",
      },
    },
  ],
  tips: [
    { fr: "Ramasse les 7 fardeaux nommés (honte, culpabilité, tristesse, colère, inquiétude, regret, solitude) en descendant la vallée, et dépose-les dans le Bassin de la Source.", en: "Pick up the 7 named burdens (shame, guilt, sorrow, anger, worry, regret, loneliness) as you walk down the valley, and lay them into the Basin of the Spring." },
    { fr: "Suis les lucioles : les 5 lanternes jalonnent la descente — place-les sur le Porche de l'Aube pour baliser la sortie de la vallée, et ramène les deux voyageurs égarés.", en: "Follow the fireflies: the 5 lanterns line the descent — set them on the Porch of Dawn to mark the way out of the valley, and bring back the two lost travelers." },
    { fr: "Tiens-toi dans le Vent de l'Esprit puis dans les eaux vives de la Source, et écoute Nicodème : la nouvelle naissance est un don d'en haut (Jean 3:3-8).", en: "Stand in the Wind of the Spirit and then in the Living waters of the Spring, and listen to Nicodemus: the new birth is a gift from above (John 3:3-8)." },
  ],
  lesson: {
    fr: "En vérité, en vérité, je te le dis, si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu. (Jean 3:3)",
    en: "Most certainly I tell you, unless one is born anew, he can't see God's Kingdom. (John 3:3)",
  },
  audio: { ambience: "glory" },
  multiplayer: { mode: "coop", maxPlayers: 8 },
};

export default parcoursDuDiscipleScene2;
